import React from 'react'
import { useTranslation } from 'react-i18next'
import { Menu, Building2, ArrowLeftRight, Settings, User, MoreVertical, Sun, Moon, Bell } from 'lucide-react'

import { Button } from '@/components/ui/button'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
import { Breadcrumbs } from './Breadcrumbs'
import { ThemeToggle } from './ThemeToggle'
import { LangToggle } from './LangToggle'
import { NotificationsBell } from '@/widgets/NotificationsBell'
import { useAuthStore } from '@/store/auth.store'
import { useUIStore } from '@/store/ui.store'
import { useSettingsStore } from '@/store/settings.store'
import { getCurrentUserRoles } from '@/core/auth'
import { createRTLClassName, tw } from '@/lib/rtl'
import { setDocumentDirection } from '@/lib/rtl'
import { formatRelativeTime } from '@/lib/format'

/**
 * Clean professional topbar without background - minimal design
 */
export const Topbar: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const { user, logout, switchBranch } = useAuthStore()
  const { sidebarOpen, setSidebarOpen, theme, setTheme, notifications, unreadNotifications, markNotificationRead } = useUIStore()
  const { language, setLanguage } = useSettingsStore()

  const handleLogout = () => {
    logout()
    window.location.href = '/login'
  }

  const handleSwitchBranch = () => {
    console.log('🔄 Switching branch...')
    switchBranch()
  }

  const handleLanguageChange = async (langCode: string) => {
    try {
      await i18n.changeLanguage(langCode)
      setLanguage(langCode as 'en' | 'ar')
      setDocumentDirection(langCode)
    } catch (error) {
      console.error('Failed to change language:', error)
    }
  }

  // Get current user roles for display
  const userRoles = getCurrentUserRoles()

  // Check if user has multiple branches to show switch button
  const hasMultipleBranches = user?.branches && user.branches.length > 1

  const languages = [
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  ]

  return (
    <header className="border-b border-gray-200 dark:border-gray-800 px-6 py-4 bg-transparent">
      <div className="flex items-center justify-between">
        {/* Left Section: Mobile Menu + Breadcrumbs */}
        <div className="flex items-center space-x-4">
          {/* Mobile hamburger menu */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden hover:bg-gray-100 dark:hover:bg-gray-800 p-2"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Menu className="w-5 h-5" />
          </Button>
          
          <Breadcrumbs />
        </div>

        {/* Right Section: Actions and User Menu */}
        <div className={createRTLClassName({
          common: 'flex items-center space-x-1',
          rtl: 'space-x-reverse',
        })}>
          {/* Desktop View: Individual Buttons (hidden on mobile) */}
          <div className="hidden lg:flex items-center space-x-1">
            {/* Switch Branch Button - only show if user has multiple branches */}
            {hasMultipleBranches && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleSwitchBranch}
                      className="h-8 w-8 p-0"
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                        <ArrowLeftRight className="w-3 h-3 text-white" />
                      </div>
                      <span className="sr-only">Switch Branch</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Switch to another branch</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Current Branch Display */}
            {user?.selectedBranch && (
              <div className="flex items-center space-x-1 px-2 py-1 rounded-lg bg-gradient-to-br from-primary/10 to-secondary/5 border border-primary/20">
                <div className="w-5 h-5 bg-gradient-to-br from-primary to-secondary rounded flex items-center justify-center">
                  <Building2 className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm font-medium text-primary hidden sm:inline">
                  {user.selectedBranch.branchName}
                </span>
              </div>
            )}

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Language Toggle */}
            <LangToggle />

            {/* Notifications */}
            <NotificationsBell />

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                    <User className="h-3 w-3 text-white" />
                  </div>
                  <span className="sr-only">User menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user?.userName || 'User'}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email || 'user@example.com'}
                    </p>
                    {user?.selectedBranch && (
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.selectedBranch.branchName}
                      </p>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {/* User Roles */}
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-wrap gap-1">
                    {userRoles.length > 0 ? (
                      userRoles.map((role) => (
                        <Badge key={role} variant="secondary" className="text-xs">
                          {role}
                        </Badge>
                      ))
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        No Role
                      </Badge>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                {/* Switch Branch in dropdown */}
                {hasMultipleBranches && (
                  <>
                    <DropdownMenuItem onClick={handleSwitchBranch}>
                      <ArrowLeftRight className={`w-4 h-4 ${tw.me('2')}`} />
                      <span>Switch Branch</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                
                <DropdownMenuItem>
                  <Settings className={`w-4 h-4 ${tw.me('2')}`} />
                  <span>{t('navigation.settings')}</span>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={handleLogout}>
                  <span>{t('auth.logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile View: Dropdown Menu (shown only on mobile) */}
          <div className="lg:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 p-0 relative">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                    <MoreVertical className="w-4 h-4 text-white" />
                  </div>
                  {unreadNotifications > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    >
                      {unreadNotifications > 99 ? '99+' : unreadNotifications}
                    </Badge>
                  )}
                  <span className="sr-only">Menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64" align="end" forceMount>
                {/* User Info */}
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user?.userName || 'User'}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email || 'user@example.com'}
                    </p>
                    {user?.selectedBranch && (
                      <div className="flex items-center space-x-1 mt-1">
                        <Building2 className="w-3 h-3 text-primary" />
                        <p className="text-xs leading-none text-muted-foreground">
                          {user.selectedBranch.branchName}
                        </p>
                      </div>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                {/* Switch Branch */}
                {hasMultipleBranches && (
                  <>
                    <DropdownMenuItem onClick={handleSwitchBranch}>
                      <div className="w-5 h-5 bg-gradient-to-br from-primary to-secondary rounded flex items-center justify-center mr-2">
                        <ArrowLeftRight className="w-3 h-3 text-white" />
                      </div>
                      <span>Switch Branch</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}

                {/* Theme Toggle */}
                <DropdownMenuLabel>Theme</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  <Sun className={`w-4 h-4 ${tw.me('2')}`} />
                  <span>Light</span>
                  {theme === 'light' && <span className="ml-auto">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>
                  <Moon className={`w-4 h-4 ${tw.me('2')}`} />
                  <span>Dark</span>
                  {theme === 'dark' && <span className="ml-auto">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuSeparator />

                {/* Language Toggle */}
                <DropdownMenuLabel>Language</DropdownMenuLabel>
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={language === lang.code ? 'bg-accent' : ''}
                  >
                    <span className={`text-base ${tw.me('2')}`}>{lang.flag}</span>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{lang.name}</span>
                      <span className="text-xs text-muted-foreground">{lang.nativeName}</span>
                    </div>
                    {language === lang.code && <span className="ml-auto">✓</span>}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />

                {/* Notifications */}
                <DropdownMenuLabel className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-gradient-to-br from-primary to-secondary rounded flex items-center justify-center mr-2">
                      <Bell className="w-3 h-3 text-white" />
                    </div>
                    <span>Notifications</span>
                  </div>
                  {unreadNotifications > 0 && (
                    <Badge variant="destructive" className="h-5 px-1.5 flex items-center justify-center text-xs">
                      {unreadNotifications > 99 ? '99+' : unreadNotifications}
                    </Badge>
                  )}
                </DropdownMenuLabel>
                {notifications.length === 0 ? (
                  <div className="px-2 py-4 text-center">
                    <Bell className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">No notifications</p>
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto">
                    {notifications.slice(0, 3).map((notification) => (
                      <DropdownMenuItem
                        key={notification.id}
                        className={`flex flex-col items-start p-2 cursor-pointer ${
                          !notification.read ? 'bg-accent/50' : ''
                        }`}
                        onClick={() => {
                          if (!notification.read) {
                            markNotificationRead(notification.id)
                          }
                        }}
                      >
                        <div className="flex items-start justify-between w-full">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-1 mb-1">
                              <Badge
                                variant={notification.type === 'error' ? 'destructive' : 'secondary'}
                                className="text-xs"
                              >
                                {notification.type}
                              </Badge>
                              {!notification.read && (
                                <div className="h-1.5 w-1.5 bg-primary rounded-full" />
                              )}
                            </div>
                            <p className="text-xs font-medium mb-0.5 truncate">
                              {notification.title}
                            </p>
                            <p className="text-xs text-muted-foreground line-clamp-1 mb-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatRelativeTime(notification.timestamp)}
                            </p>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                    {notifications.length > 3 && (
                      <div className="px-2 py-1 text-center">
                        <p className="text-xs text-muted-foreground">
                          {notifications.length - 3} more notifications
                        </p>
                      </div>
                    )}
                  </div>
                )}
                <DropdownMenuSeparator />

                {/* Settings */}
                <DropdownMenuItem>
                  <div className="w-5 h-5 bg-gradient-to-br from-primary to-secondary rounded flex items-center justify-center mr-2">
                    <Settings className="w-3 h-3 text-white" />
                  </div>
                  <span>{t('navigation.settings')}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />

                {/* Logout */}
                <DropdownMenuItem onClick={handleLogout}>
                  <span>{t('auth.logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  )
}

